import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../ui/alert-dialog';
import { Plus, Pencil, Trash2, Search, Building2, BarChart3 } from 'lucide-react';
import { apiListCustomers, apiCreateCustomer, apiUpdateCustomer, apiDeleteCustomer } from '../../api';
import { toast } from 'sonner';

const empty = { name: '', referente: '', notes: '', active: true };

const CustomerForm = ({ initial, onSubmit, onCancel }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState(initial ? { ...initial } : empty);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Ragione sociale obbligatoria');
      return;
    }
    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="customer-form">
      {isEdit && (
        <div className="text-xs text-slate-500">
          Codice cliente: <span className="font-mono font-semibold text-slate-800">{initial.code}</span>
        </div>
      )}
      <div className="space-y-2">
        <Label>Ragione sociale <span className="text-red-500">*</span></Label>
        <Input
          data-testid="customer-name-input"
          placeholder="es. Acme S.p.A."
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
        />
      </div>
      <div className="space-y-2">
        <Label>Referente</Label>
        <Input
          data-testid="customer-referente-input"
          placeholder="Nome e cognome"
          value={form.referente}
          onChange={(e) => setForm({ ...form, referente: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label>Note</Label>
        <Textarea
          rows={3}
          placeholder="Condizioni di pagamento, contatti, ecc."
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          className="resize-none"
        />
      </div>
      <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200">
        <div className="text-sm font-medium">Cliente attivo</div>
        <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>Annulla</Button>
        <Button data-testid="customer-submit-btn" type="submit" className="bg-blue-700 hover:bg-blue-800">
          {isEdit ? 'Salva' : 'Crea cliente'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const CustomersManager = () => {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const reload = () => apiListCustomers().then(setList).catch(() => setList([]));
  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = list.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.name.toLowerCase().includes(q) ||
           c.code.toLowerCase().includes(q) ||
           (c.referente || '').toLowerCase().includes(q);
  });

  const handleSubmit = async (data) => {
    try {
      if (editing) {
        await apiUpdateCustomer(editing.id, data);
        toast.success('Cliente aggiornato');
      } else {
        await apiCreateCustomer(data);
        toast.success('Cliente creato');
      }
      setDialogOpen(false);
      setEditing(null);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await apiDeleteCustomer(toDelete.id);
      toast.success('Cliente eliminato');
      setToDelete(null);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore eliminazione');
    }
  };

  return (
    <div className="space-y-4" data-testid="customers-manager">
      <div className="flex items-center justify-between gap-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="customer-search"
            placeholder="Cerca per ragione sociale, codice o referente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 pl-8 w-[320px] text-sm"
          />
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button
              data-testid="customer-new-btn"
              onClick={() => { setEditing(null); setDialogOpen(true); }}
              className="bg-blue-700 hover:bg-blue-800 gap-2"
            >
              <Plus className="w-4 h-4" /> Nuovo cliente
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? 'Modifica cliente' : 'Nuovo cliente'}</DialogTitle>
              <DialogDescription>
                {editing ? 'Aggiorna le informazioni del cliente.' : 'Il codice cliente verrà generato automaticamente (CLI-XXX).'}
              </DialogDescription>
            </DialogHeader>
            <CustomerForm
              initial={editing}
              onSubmit={handleSubmit}
              onCancel={() => { setDialogOpen(false); setEditing(null); }}
            />
          </DialogContent>
        </Dialog>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-xl border border-dashed border-slate-300">
          <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="text-sm font-medium text-slate-700">Nessun cliente</div>
          <div className="text-xs text-slate-500 mt-1">Crea il primo cliente per iniziare</div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase text-slate-600">Codice</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase text-slate-600">Ragione sociale</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase text-slate-600">Referente</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase text-slate-600">Note</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold uppercase text-slate-600">Stato</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold uppercase text-slate-600">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(c => (
                <tr key={c.id} className={`hover:bg-slate-50 ${!c.active ? 'opacity-60' : ''}`}>
                  <td className="px-4 py-3 font-mono font-medium text-slate-900">{c.code}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{c.name}</td>
                  <td className="px-4 py-3 text-slate-700">{c.referente || '—'}</td>
                  <td className="px-4 py-3 text-slate-500 truncate max-w-[260px]">{c.notes || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${c.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                      {c.active ? 'Attivo' : 'Disattivato'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      data-testid={`customer-detail-${c.code}`}
                      size="sm" variant="ghost" className="h-8 px-2 text-blue-700 hover:bg-blue-50"
                      onClick={() => navigate(`/production/customers/${c.id}`)}
                      title="Vedi dettaglio commessa"
                    >
                      <BarChart3 className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      data-testid={`customer-edit-${c.code}`}
                      size="sm" variant="ghost" className="h-8 px-2"
                      onClick={() => { setEditing(c); setDialogOpen(true); }}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      data-testid={`customer-delete-${c.code}`}
                      size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                      onClick={() => setToDelete(c)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Saranno eliminate anche tutte le lavorazioni di catalogo associate. I clienti con registrazioni di produzione non possono essere eliminati (devono essere disattivati).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CustomersManager;
